<template>
  <div id="playlist-recent-songs">
    <h4>Recent Songs</h4>
    <Vuetable
      data-path="songs"
      pagination-path="pagination"
      ref="vuetable"
      :data="paginatedData"
      :api-mode="false"
      :fields="fields"
      :css="css.table"
      :per-page="10"
      @vuetable:pagination-data="onPaginationData">

      <template slot="preview" slot-scope="props">
        <vue-plyr :ref="`plyr-${props.rowData.id}`">
          <audio :id="props.rowData.id">
            <source :src="props.rowData.url" type="audio/mp3" />
          </audio>
        </vue-plyr>
      </template>

      <template slot="action" slot-scope="props">
        <b-button
          variant="success"
          @click="checkSong(props.rowData)"
          :disabled="playlistSongIds.includes(props.rowData.id)"
          block>
          Add
        </b-button>
      </template>

      <template slot="length" slot-scope="props">
        {{ formatTime(props.rowData.length) }}
      </template>
    </Vuetable>

    <div class="d-flex align-items-center justify-content-end">
      <VuetablePaginationInfo ref="paginationInfo" />
      <VuetablePagination
        ref="pagination"
        @vuetable-pagination:change-page="onChangePage"
        :css="css.pagination"
      />
    </div>
  </div>
</template>

<script>
  import TableStyle from '../table-bootstrap-css.js';
  export default {
    props : {
      data : {
        type : Object,
        default: () => ({ songs: [], pagination: {} })
      },
    },
    data() {
      return {
        loading : false,
        css     : TableStyle,
        fields  : [ {
          name      : '__slot:action',
          dataClass : 'text-center',
        }, {
          name  : 'title',
          title : 'Title',
        }, {
          name  : 'album',
          title : 'Album',
        }, {
          name       : '__slot:preview',
          title      : 'Preview',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__slot:length',
          title      : 'Length',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        } ],
        currentPage: 1,
        perPage: 10,
      }
    },
    computed : {
      playlistSongIds() {
        const allSongs = this.data && this.data.songs ? this.data.songs : [];
        return this.$store.getters['playlist/playlistSongIds'];
      },
      paginatedData() {
        const allSongs = this.data && this.data.songs ? this.data.songs : [];

        const totalSongs = allSongs.length;
        const lastPage = Math.ceil(totalSongs / this.perPage);
        // Asegúrate de que currentPage no sea menor que 1 o mayor que lastPage
        const currentPageSafe = Math.max(1, Math.min(this.currentPage, lastPage || 1));

        const from = (currentPageSafe - 1) * this.perPage;
        const to = from + this.perPage;
        const songsForCurrentPage = allSongs.slice(from, to);

        return {
          songs: songsForCurrentPage,
          pagination: {
            total: totalSongs,
            per_page: this.perPage,
            current_page: currentPageSafe, // Usar la página segura
            last_page: lastPage,
            // Mantener estas URLs como null o 'dummy' para paginación de cliente
            // VuetablePagination operará sobre current_page y last_page.
            next_page_url: currentPageSafe < lastPage ? 'dummy_next_url' : null,
            prev_page_url: currentPageSafe > 1 ? 'dummy_prev_url' : null,
            from: from + 1,
            to: Math.min(to, totalSongs),
          }
        };
      }
    },
    components : {
      'Vuetable'           : () => import('vuetable-2/src/components/Vuetable'),
      'VuetablePagination' :
        () => import('vuetable-2/src/components/VuetablePagination'),
      'VuetablePaginationInfo' :
        () => import('vuetable-2/src/components/VuetablePaginationInfo'),
    },
    methods : {
      /**
       * Check if Song is already added
       * @param data
       */
      checkSong(data) {
        console.log(data, '[RECIENTES]');
        const isAdded = this.playlistSongIds.includes(data.id);

        if (!isAdded)
          this.addToPlaylist(data.id);
        else {
          this.$modal.show('dialog', {
            title   : 'Duplicate Song',
            text    : `<b>${data.title}</b> is already in your playlist`,
            buttons : [{
              title : 'Skip Duplicate',
            }],
          });
        }
      },

      /**
       * Add Song to Playlist
       * @param musicId
       */
      addToPlaylist(musicId) {
        this.$http.post('api/playlist/song', {
          mPlaylistId : this.$route.params.playlistId,
          musicId     : musicId,
        }).then(() => {
          this.$store.dispatch(
            'playlist/getPlaylistSongIds',
            this.$route.params.playlistId);
          this.$emit('song-added');
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success',
            text  : 'Added song successfully!',
          });
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'failed',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        });
      },

      /**
       * Update Paginaton info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$nextTick(() => {
          this.$refs.pagination.setPaginationData(paginationData);
          this.$refs.paginationInfo.setPaginationData(paginationData);
        });
      },

      /**
       * Update Vuetable page
       * @param page
       */
      onChangePage(page) {
        // Asegúrate de que 'page' sea un número válido antes de asignarlo
        if (typeof page === 'number' && page > 0) {
          this.currentPage = page;
        } else {
          // Si 'page' no es un número (ej. 'next' o 'prev' o undefined),
          // intentamos inferir la nueva página
          const totalSongs = this.data && this.data.songs ? this.data.songs.length : 0;
          const lastPage = Math.ceil(totalSongs / this.perPage);

          if (page === 'next') {
            this.currentPage = Math.min(this.currentPage + 1, lastPage);
          } else if (page === 'prev') {
            this.currentPage = Math.max(1, this.currentPage - 1);
          } else if (page === 'first') { // Para el botón de "primera página"
            this.currentPage = 1;
          } else if (page === 'last') { // Para el botón de "última página"
            this.currentPage = lastPage;
          }
          // Si page es un número de página específico que VuetablePagination envía (ej. 1, 2, 3)
          // entonces currentPage ya se actualizó correctamente en el if inicial.
        }

        // Vuetable se re-renderizará automáticamente con el nuevo currentPage
        // y la computed property 'paginatedData' se actualizará.
      },

      /**
       * Format Song Length
       * @param value
       */
      formatTime(value) {
        let result = '';
        const hours = Math.floor(value / 3600);
        const minutes = Math.floor((value - (hours * 3600)) / 60);
        let seconds = value - (hours * 3600) - (minutes * 60);

        // round seconds & exclude remainder
        seconds = parseInt(Math.round(seconds * 100) / 100);

        if (hours > 0) {
          result = (hours < 10 ? '0' + hours : hours) +
            ':' + (minutes < 10 ? '0' + minutes : minutes) +
            ':' + (seconds < 10 ? '0' + seconds : seconds);
        } else {
          result = (minutes < 10 ? '0' + minutes : minutes) + ':' +
            (seconds < 10 ? '0' + seconds : seconds);
        }

        return result;
      },
    },
  }
</script>

<style lang="scss" scoped>

</style>